// Super User: Operational architect / power user
// Configures dashboards, workflows, metrics, alerts, features, and training for the organization

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, Bell, BookOpen, Video, MessageCircle } from 'lucide-react';
import { InitiativesDB } from '../../utils/initiativesData';
import { AssignmentDB } from '../../utils/assignmentsData';
import { EmailSignalEngine } from '../../utils/emailSignalData';
import { Button } from '../ui/button';
import { StatusBadge } from '../ui/StatusBadge';

const TABS = [
  { id: 'overview', label: 'Overview', path: '/super-user/overview' },
  { id: 'dashboards', label: 'Dashboard Config', path: '/super-user/dashboards' },
  { id: 'workflows', label: 'Workflows & Tasks', path: '/super-user/workflows' },
  { id: 'metrics', label: 'Metrics & Thresholds', path: '/super-user/metrics' },
  { id: 'alerts', label: 'Alerts & Escalations', path: '/super-user/alerts' },
  { id: 'features', label: 'Feature Management', path: '/super-user/features' },
  { id: 'training', label: 'Training & Support', path: '/super-user/training' }
];

function SuperUserDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const pathTab = (location.pathname.match(/\/super-user\/(.+)/) || [])[1] || 'overview';
  const [activeTab, setActiveTab] = useState(pathTab);

  useEffect(() => {
    setActiveTab(pathTab);
  }, [pathTab]);

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Super User Configuration Center</h1>
        <p className="dashboard-subtitle">
          Operational architecture and platform configuration
        </p>
      </div>

      <nav className="flex gap-1 border-b border-nn-border mb-4 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabClick(tab)}
            className={`tab${activeTab === tab.id ? ' active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div style={{ marginTop: '24px' }}>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'dashboards' && <DashboardConfigTab />}
        {activeTab === 'workflows' && <WorkflowsTab />}
        {activeTab === 'metrics' && <MetricsTab />}
        {activeTab === 'alerts' && <AlertsTab />}
        {activeTab === 'features' && <FeaturesTab />}
        {activeTab === 'training' && <TrainingTab />}
      </div>
    </div>
  );
}

function OverviewTab() {
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">System Health & Usage</h2>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-5">
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Users (Last 30 Days)</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">247</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">12% vs last month</div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Configured Markets</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">8/10</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">2 pending setup</div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Alerts</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">15</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">3 require attention</div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
          <div className="p-5">
            <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">System Uptime</div>
            <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">99.8%</div>
            <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">Above target</div>
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '40px' }}>
        <h3 className="section-title">Recent Configuration Changes</h3>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5">
          <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: '2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span><strong>West Region</strong> - Updated staffing thresholds</span>
              <span style={{ color: '#9CA3AF' }}>2 hours ago</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span><strong>Primary Care</strong> - Enabled new quality metrics</span>
              <span style={{ color: '#9CA3AF' }}>5 hours ago</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <span><strong>All Markets</strong> - Updated daily task checklist template</span>
              <span style={{ color: '#9CA3AF' }}>Yesterday</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span><strong>North MOB</strong> - Configured escalation rules</span>
              <span style={{ color: '#9CA3AF' }}>2 days ago</span>
            </div>
          </div>
        </div>
      </div>

      <div className="section-header" style={{ marginTop: '40px' }}>
        <h3 className="section-title">Quick Actions</h3>
      </div>

      <div className="grid grid-cols-3 gap-5">
        <Button type="button" variant="ghost" className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ cursor: 'pointer', textAlign: 'left', padding: 0, height: 'auto', width: '100%', display: 'block' }}>
          <div className="p-5">
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚙️</div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>Configure New Market</div>
            <div className="text-body text-text-muted">Set up dashboards and workflows for a new market</div>
          </div>
        </Button>

        <Button type="button" variant="ghost" className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ cursor: 'pointer', textAlign: 'left', padding: 0, height: 'auto', width: '100%', display: 'block' }}>
          <div className="p-5">
            <div style={{ marginBottom: '8px' }}><ClipboardList size={24} strokeWidth={2} color="#0891B2" /></div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>Create Task Template</div>
            <div className="text-body text-text-muted">Build standardized checklists for daily operations</div>
          </div>
        </Button>

        <Button type="button" variant="ghost" className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ cursor: 'pointer', textAlign: 'left', padding: 0, height: 'auto', width: '100%', display: 'block' }}>
          <div className="p-5">
            <div style={{ marginBottom: '8px' }}><Bell size={24} strokeWidth={2} color="#0891B2" /></div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '4px' }}>Set Up Alert</div>
            <div className="text-body text-text-muted">Define thresholds and escalation paths</div>
          </div>
        </Button>
      </div>
    </div>
  );
}

function DashboardConfigTab() {
  const [selectedMarket, setSelectedMarket] = useState('west');

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Dashboard Configuration</h2>
        <select
          value={selectedMarket}
          onChange={(e) => setSelectedMarket(e.target.value)}
          className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua"
        >
          <option value="all">All Markets</option>
          <option value="west">West Region</option>
          <option value="north">North Region</option>
          <option value="south">South Region</option>
          <option value="east">East Region</option>
        </select>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: '20px' }}>
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Director/VP Dashboard</div>
        </div>
        <div className="p-5">
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 6 }}>Default View</label>
            <select className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua" style={{ width: '100%' }}>
              <option>Service Line Overview</option>
              <option>Quality Dashboard</option>
              <option>Financial Summary</option>
            </select>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 6 }}>Enabled Tabs</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['Overview', 'Team Hierarchy', 'Access', 'Quality', 'Staffing', 'Finance', 'Initiatives', 'Consumer Insights'].map((tab) => (
                <label key={tab} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" defaultChecked style={{ width: 14, height: 14 }} />
                  <span>{tab}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
            <Button variant="primary" size="sm" type="button">Save Configuration</Button>
            <Button variant="default" size="sm" type="button">Reset to Default</Button>
          </div>
        </div>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Manager Dashboard</div>
        </div>
        <div className="p-5">
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 6 }}>Default View</label>
            <select className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua" style={{ width: '100%' }}>
              <option>Priority Queue</option>
              <option>Quality Dashboard</option>
              <option>Team Performance</option>
            </select>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 6 }}>Enabled Tabs</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {['Overview', 'Access', 'Quality', 'Staffing', 'Finance', 'Initiatives', 'Consumer Insights'].map((tab) => (
                <label key={tab} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" defaultChecked style={{ width: 14, height: 14 }} />
                  <span>{tab}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
            <Button variant="primary" size="sm" type="button">Save Configuration</Button>
            <Button variant="default" size="sm" type="button">Reset to Default</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowsTab() {
  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Task Templates & Checklists</h2>
        <Button variant="primary" size="sm" type="button">+ Create New Template</Button>
      </div>

      {[
        { title: 'Daily Manager Checklist', badge: 'Active in 8 markets', tasks: 'Review overnight incidents, Check staffing levels, Approve schedule changes, Review priority queue, Update team huddle board, Check wait times, Review quality alerts, End-of-day summary', count: 8 },
        { title: 'Weekly Manager Checklist', badge: 'Active in 8 markets', tasks: 'Review weekly metrics, Conduct team 1:1s, Update initiatives tracker, Review patient feedback, Submit budget variance report, Plan next week schedule', count: 6 },
        { title: 'Monthly Director Checklist', badge: 'Active in 5 service lines', tasks: 'Review service line performance, Conduct manager check-ins, Review financial variance, Update strategic initiatives, Quality improvement review, Staffing plan review, Patient satisfaction review, Budget planning, Leadership reporting, Identify improvement opportunities', count: 10 },
        { title: 'Quarterly Strategic Review', badge: 'Active organization-wide', tasks: 'QBR preparation, Strategic planning session, Budget forecast, Staffing model review, Technology roadmap update, Training needs assessment, Performance improvement plans, Market competitive analysis', count: 8 }
      ].map((tpl) => (
        <div key={tpl.title} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: '20px' }}>
          <div className="px-5 py-4 flex justify-between items-center">
            <div className="font-heading text-[14px] font-semibold text-text-dark">{tpl.title}</div>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-surface-warm text-text-dark-secondary border border-nn-border">{tpl.badge}</span>
          </div>
          <div className="p-5">
            <div className="text-body text-text-muted" style={{ marginBottom: 12 }}>
              <strong>Tasks ({tpl.count}):</strong> {tpl.tasks}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button variant="default" size="sm" type="button">Edit Template</Button>
              <Button variant="default" size="sm" type="button">View Usage Analytics</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MetricsTab() {
  const metricTables = [
    { category: 'Quality Metrics', rows: [
      { metric: 'Overall Quality Score', target: '≥90%', warning: '80-89%', critical: '<80%' },
      { metric: 'Gap Closure Rate', target: '≥75%', warning: '60-74%', critical: '<60%' },
      { metric: 'Patient Safety Events', target: '0', warning: '1-2', critical: '≥3' }
    ]},
    { category: 'Access Metrics', rows: [
      { metric: 'Avg Wait Time', target: '≤15 min', warning: '16-25 min', critical: '>25 min' },
      { metric: '3rd Next Available', target: '≤7 days', warning: '8-14 days', critical: '>14 days' },
      { metric: 'No-Show Rate', target: '≤5%', warning: '6-10%', critical: '>10%' }
    ]},
    { category: 'Staffing Metrics', rows: [
      { metric: 'Staffing Coverage', target: '100%', warning: '90-99%', critical: '<90%' },
      { metric: 'Overtime %', target: '≤3%', warning: '4-6%', critical: '>6%' },
      { metric: 'Turnover Rate', target: '≤10%', warning: '11-15%', critical: '>15%' }
    ]}
  ];

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Metrics Configuration</h2>
        <p className="section-subtitle">Define thresholds and targets for organizational metrics</p>
      </div>

      {metricTables.map(({ category, rows }) => (
        <div key={category} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: '20px' }}>
          <div className="px-5 py-4 flex justify-between items-center">
            <div className="font-heading text-[14px] font-semibold text-text-dark">{category}</div>
          </div>
          <div className="p-5">
            <table className="nn-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th style={{ textAlign: 'center' }}>Target</th>
                  <th style={{ textAlign: 'center', color: '#854D0E' }}>Warning</th>
                  <th style={{ textAlign: 'center', color: '#991B1B' }}>Critical</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.metric}>
                    <td>{row.metric}</td>
                    <td style={{ textAlign: 'center' }}>{row.target}</td>
                    <td style={{ textAlign: 'center', color: '#854D0E' }}>{row.warning}</td>
                    <td style={{ textAlign: 'center', color: '#991B1B' }}>{row.critical}</td>
                    <td style={{ textAlign: 'center' }}>
                      <Button variant="default" size="sm" type="button">Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button variant="primary" size="sm" type="button">+ Add New Metric</Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AlertsTab() {
  const alerts = [
    { name: 'Critical Wait Time', condition: 'Avg wait time > 30 min for 2 hours', notify: 'Manager → Director' },
    { name: 'Quality Score Drop', condition: 'Score drops below 85% for 1 week', notify: 'Director → VP' },
    { name: 'Staffing Coverage', condition: 'Coverage < 90% within 24 hours', notify: 'Manager → Staffing Coordinator' },
    { name: 'Budget Variance', condition: 'Monthly variance > 5%', notify: 'Director → Finance' }
  ];

  const escalationLevels = [
    { level: 'Level 1: Immediate (0-2 hours)', desc: 'Manager notified → Action required within 2 hours → Auto-escalate if not resolved' },
    { level: 'Level 2: Urgent (2-24 hours)', desc: 'Director notified → Action required within 24 hours → VP notified at 12-hour mark' },
    { level: 'Level 3: Important (1-3 days)', desc: 'Director notified → Review required within 3 days → Weekly summary to VP' },
    { level: 'Level 4: Informational (7+ days)', desc: 'Included in routine reporting → No immediate action required → Monthly review' }
  ];

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Alert Configuration</h2>
        <Button variant="primary" size="sm" type="button">+ Create New Alert Rule</Button>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: '20px' }}>
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Active Alert Rules (12)</div>
        </div>
        <div className="p-5">
          <div>
            {alerts.map((alert) => (
              <div key={alert.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}>
                <div>
                  <div className="text-body font-medium">{alert.name}</div>
                  <div className="text-body-sm text-text-muted" style={{ marginTop: 4 }}><strong>Trigger:</strong> {alert.condition}</div>
                  <div className="text-body-sm text-text-muted"><strong>Escalation:</strong> {alert.notify}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <StatusBadge variant="on-track">Active</StatusBadge>
                  <Button variant="default" size="sm" type="button">Edit</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Escalation Paths</div>
        </div>
        <div className="p-5">
          <div>
            {escalationLevels.map(({ level, desc }) => (
              <div key={level} style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}>
                <div className="text-body font-medium" style={{ marginBottom: 4 }}>{level}</div>
                <div className="text-body text-text-muted">{desc}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
            <Button variant="default" size="sm" type="button">Edit Escalation Rules</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const STRATEGIC_INITIATIVES_ROLES = [
  { value: 'super-user', label: 'Super User (always enabled)', alwaysOn: true },
  { value: 'director', label: 'Director/VP' },
  { value: 'manager', label: 'Manager' }
];

const ASSIGNMENT_SYSTEM_ROLES = [
  { value: 'super-user', label: 'Super User (always enabled)', alwaysOn: true },
  { value: 'director', label: 'Director/VP' },
  { value: 'manager', label: 'Manager' }
];

const EMAIL_SIGNAL_ROLES = [
  { value: 'super-user', label: 'Super User (always enabled)', alwaysOn: true },
  { value: 'director', label: 'Director/VP' },
  { value: 'manager', label: 'Manager' }
];

function FeaturesTab() {
  const [strategicEnabled, setStrategicEnabled] = useState(false);
  const [strategicRoles, setStrategicRoles] = useState([]);
  const [strategicSaved, setStrategicSaved] = useState(false);
  const [assignmentEnabled, setAssignmentEnabled] = useState(false);
  const [assignmentRoles, setAssignmentRoles] = useState([]);
  const [assignmentSaved, setAssignmentSaved] = useState(false);
  const [emailSignalEnabled, setEmailSignalEnabled] = useState(false);
  const [emailSignalRoles, setEmailSignalRoles] = useState([]);
  const [emailSignalSaved, setEmailSignalSaved] = useState(false);

  useEffect(() => {
    const toggle = InitiativesDB.getFeatureToggle();
    if (toggle) {
      setStrategicEnabled(!!toggle.enabled);
      setStrategicRoles(toggle.enabledForRoles || ['super-user']);
    }
    const assignmentToggle = AssignmentDB.getFeatureToggle();
    if (assignmentToggle) {
      setAssignmentEnabled(!!assignmentToggle.enabled);
      setAssignmentRoles(assignmentToggle.enabledForRoles || []);
    }
    const emailToggle = EmailSignalEngine.getFeatureToggle();
    if (emailToggle) {
      setEmailSignalEnabled(!!emailToggle.enabled);
      setEmailSignalRoles(emailToggle.enabledForRoles || []);
    }
  }, []);

  const handleStrategicRoleChange = (roleValue, checked) => {
    if (roleValue === 'super-user') return;
    setStrategicRoles(prev =>
      checked ? [...prev.filter(r => r !== roleValue), roleValue] : prev.filter(r => r !== roleValue)
    );
  };

  const handleSaveStrategicToggle = () => {
    const enabledForRoles = strategicEnabled
      ? [...new Set([...strategicRoles, 'super-user'])]
      : ['super-user'];
    InitiativesDB.setFeatureToggle({
      enabled: strategicEnabled,
      enabledForRoles,
      enabledForUsers: []
    });
    setStrategicSaved(true);
    setTimeout(() => setStrategicSaved(false), 2000);
  };

  const handleSeedDemo = () => {
    if (window.confirm('Add demo Strategic Initiative data? This will create one sample initiative with KPIs and a status update.')) {
      InitiativesDB.seedDemoData();
      alert('Demo data added. Go to Strategic Initiatives to see it.');
    }
  };

  const handleSeedEnhancedDemo = () => {
    if (window.confirm('Add one more enhanced demo initiative (full KPIs, tags, milestones, status update)?')) {
      InitiativesDB.seedEnhancedDemoData();
      alert('Enhanced demo initiative added. Reload or go to Strategic Initiatives to see it.');
    }
  };

  const handleAssignmentRoleChange = (roleValue, checked) => {
    if (roleValue === 'super-user') return;
    setAssignmentRoles(prev =>
      checked ? [...prev.filter(r => r !== roleValue), roleValue] : prev.filter(r => r !== roleValue)
    );
  };

  const handleSaveAssignmentToggle = () => {
    const enabledForRoles = assignmentEnabled
      ? [...new Set([...assignmentRoles, 'super-user'])]
      : ['super-user'];
    AssignmentDB.setFeatureToggle({
      enabled: assignmentEnabled,
      enabledForRoles,
      enabledForUsers: []
    });
    setAssignmentSaved(true);
    setTimeout(() => setAssignmentSaved(false), 2000);
  };

  const handleEmailSignalRoleChange = (roleValue, checked) => {
    if (roleValue === 'super-user') return;
    setEmailSignalRoles(prev =>
      checked ? [...prev.filter(r => r !== roleValue), roleValue] : prev.filter(r => r !== roleValue)
    );
  };

  const handleSaveEmailSignalToggle = () => {
    const enabledForRoles = emailSignalEnabled
      ? [...new Set([...emailSignalRoles, 'super-user'])]
      : ['super-user'];
    EmailSignalEngine.setFeatureToggle({
      enabled: emailSignalEnabled,
      enabledForRoles,
      enabledForUsers: []
    });
    setEmailSignalSaved(true);
    setTimeout(() => setEmailSignalSaved(false), 2000);
  };

  const directorFeatures = [
    { name: 'Team Hierarchy View', enabled: true },
    { name: 'Drill-Down Capability', enabled: true },
    { name: 'Performance Buckets', enabled: true },
    { name: 'Initiative Tracking', enabled: true },
    { name: 'Budget Variance Alerts', enabled: true },
    { name: 'Consumer Insights Dashboard', enabled: true },
    { name: 'Predictive Analytics (Beta)', enabled: false }
  ];
  const managerFeatures = [
    { name: 'Priority Queue', enabled: true },
    { name: 'Daily Task Checklists', enabled: true },
    { name: 'Team Performance View', enabled: true },
    { name: 'Finance Tab', enabled: true },
    { name: 'Initiative Submissions', enabled: true },
    { name: 'Patient Feedback Access', enabled: true },
    { name: 'AI Task Recommendations (Beta)', enabled: false }
  ];

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Feature Management</h2>
        <p className="section-subtitle">Enable or disable features by market or service line</p>
      </div>

      {/* Strategic Initiatives feature toggle (pilot) */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: 24 }}>
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Strategic Initiatives (Pilot)</div>
        </div>
        <div className="p-5">
          <p className="text-body text-text-muted" style={{ marginBottom: 16 }}>
            Strategic Initiatives tracks organizational initiatives with KPIs, milestones, tags, and status updates. Data is stored in the browser (LocalStorage). When enabled for a role, it appears under that role&apos;s <strong>Initiatives</strong> tab (Unit Manager and Director/VP). Check the roles below and click Save Feature Settings.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--surface-warm)', marginBottom: 16, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={strategicEnabled}
              onChange={(e) => setStrategicEnabled(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Enable Strategic Initiatives</span>
          </label>
          {strategicEnabled && (
            <div style={{ marginLeft: 24, marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 8 }}>Enable for roles:</p>
              {STRATEGIC_INITIATIVES_ROLES.map(({ value, label, alwaysOn }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alwaysOn || strategicRoles.includes(value)}
                    onChange={(e) => handleStrategicRoleChange(value, e.target.checked)}
                    disabled={alwaysOn}
                    style={{ width: 14, height: 14, cursor: 'pointer' }}
                  />
                  <span className="text-body">{label}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
            <Button variant="primary" size="sm" type="button" onClick={handleSaveStrategicToggle}>
              {strategicSaved ? 'Saved!' : 'Save Feature Settings'}
            </Button>
            <Button variant="default" size="sm" type="button" onClick={handleSeedDemo}>
              Seed Demo Data
            </Button>
            <Button variant="default" size="sm" type="button" onClick={handleSeedEnhancedDemo}>
              Seed Enhanced Demo Data
            </Button>
          </div>
        </div>
      </div>

      {/* Assignment System feature toggle */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: 24 }}>
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Assignment System</div>
        </div>
        <div className="p-5">
          <p className="text-body text-text-muted" style={{ marginBottom: 16 }}>
            Assignment System allows assigning tasks, alerts, and initiatives to team members with workload tracking and smart constraints. Data is stored in the browser (LocalStorage). When enabled for a role, it appears as an <strong>Assignments</strong> tab. Check the roles below and click Save Feature Settings.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--surface-warm)', marginBottom: 16, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={assignmentEnabled}
              onChange={(e) => setAssignmentEnabled(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Enable Assignment System</span>
          </label>
          {assignmentEnabled && (
            <div style={{ marginLeft: 24, marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 8 }}>Enable for roles:</p>
              {ASSIGNMENT_SYSTEM_ROLES.map(({ value, label, alwaysOn }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alwaysOn || assignmentRoles.includes(value)}
                    onChange={(e) => handleAssignmentRoleChange(value, e.target.checked)}
                    disabled={alwaysOn}
                    style={{ width: 14, height: 14, cursor: 'pointer' }}
                  />
                  <span className="text-body">{label}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
            <Button variant="primary" size="sm" type="button" onClick={handleSaveAssignmentToggle}>
              {assignmentSaved ? 'Saved!' : 'Save Feature Settings'}
            </Button>
          </div>
        </div>
      </div>

      {/* Email Signal Engine feature toggle */}
      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: 24 }}>
        <div className="px-5 py-4 flex justify-between items-center">
          <div className="font-heading text-[14px] font-semibold text-text-dark">Email Signal Engine (Pilot)</div>
        </div>
        <div className="p-5">
          <p className="text-body text-text-muted" style={{ marginBottom: 16 }}>
            Email Signal Engine makes operational strain visible from email metadata (headers only). Includes Morning Brief, KPI dashboard (aging, latency, escalation, loop density), risk scoring, and intervention queue with draft messages. When enabled for a role, it appears as <strong>Email Signals</strong> in the sidebar.
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--surface-warm)', marginBottom: 16, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={emailSignalEnabled}
              onChange={(e) => setEmailSignalEnabled(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Enable Email Signal Engine</span>
          </label>
          {emailSignalEnabled && (
            <div style={{ marginLeft: 24, marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 8 }}>Enable for roles:</p>
              {EMAIL_SIGNAL_ROLES.map(({ value, label, alwaysOn }) => (
                <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alwaysOn || emailSignalRoles.includes(value)}
                    onChange={(e) => handleEmailSignalRoleChange(value, e.target.checked)}
                    disabled={alwaysOn}
                    style={{ width: 14, height: 14, cursor: 'pointer' }}
                  />
                  <span className="text-body">{label}</span>
                </label>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
            <Button variant="primary" size="sm" type="button" onClick={handleSaveEmailSignalToggle}>
              {emailSignalSaved ? 'Saved!' : 'Save Feature Settings'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-dark)', marginBottom: 6 }}>Select Market / Service Line</label>
        <select className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua" style={{ minWidth: 300 }}>
          <option>All Markets</option>
          <option>West Region</option>
          <option>North Region - Primary Care</option>
          <option>South Region - Specialty</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
          <div className="px-5 py-4 flex justify-between items-center">
            <div className="font-heading text-[14px] font-semibold text-text-dark">Director/VP Features</div>
          </div>
          <div className="p-5">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {directorFeatures.map((f) => (
                <label key={f.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-dark)' }}>{f.name}</span>
                  <input type="checkbox" defaultChecked={f.enabled} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button variant="primary" size="sm" type="button">Save Changes</Button>
            </div>
          </div>
        </div>

        <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
          <div className="px-5 py-4 flex justify-between items-center">
            <div className="font-heading text-[14px] font-semibold text-text-dark">Manager Features</div>
          </div>
          <div className="p-5">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {managerFeatures.map((f) => (
                <label key={f.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-dark)' }}>{f.name}</span>
                  <input type="checkbox" defaultChecked={f.enabled} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
              <Button variant="primary" size="sm" type="button">Save Changes</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrainingTab() {
  const modules = [
    { title: 'Director Onboarding', desc: 'Complete guide for Director/VP users', mods: 8, duration: '2 hours', completion: '94%' },
    { title: 'Manager Onboarding', desc: 'Complete guide for Manager users', mods: 10, duration: '3 hours', completion: '89%' },
    { title: 'Priority Queue Management', desc: 'How to effectively use the priority queue', mods: 5, duration: '45 min', completion: '92%' },
    { title: 'Metrics & Analytics', desc: 'Understanding dashboards and reports', mods: 6, duration: '1 hour', completion: '87%' }
  ];

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Training & Support Resources</h2>
        <Button variant="primary" size="sm" type="button">+ Add Training Module</Button>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {modules.map((m) => (
          <div key={m.title} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
            <div className="p-5">
              <div style={{ marginBottom: 8 }}><BookOpen size={28} strokeWidth={2} color="#2C5FA8" /></div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{m.title}</div>
              <div className="text-body text-text-muted" style={{ marginBottom: 8 }}>{m.desc}</div>
              <div className="text-body-sm text-text-muted" style={{ marginBottom: 12 }}>
                <strong>Modules:</strong> {m.mods} | <strong>Duration:</strong> {m.duration} | <strong>Completion Rate:</strong> {m.completion}
              </div>
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-nn-border">
                <Button variant="default" size="sm" type="button">Edit Module</Button>
                <Button variant="default" size="sm" type="button">View Analytics</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="section-header" style={{ marginTop: '40px' }}>
        <h3 className="section-title">Support Resources</h3>
      </div>

      <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
        <div className="p-5">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, fontSize: 13 }}>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><BookOpen size={16} strokeWidth={2} /> Documentation</div>
              <div className="text-text-muted">User guides, FAQs, and best practices</div>
            </div>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><Video size={16} strokeWidth={2} /> Video Tutorials</div>
              <div className="text-text-muted">Step-by-step video walkthroughs</div>
            </div>
            <div>
              <div style={{ fontWeight: 500, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><MessageCircle size={16} strokeWidth={2} /> Live Support</div>
              <div className="text-text-muted">Chat, email, and phone support</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuperUserDashboard;
